import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { Container, Typography, Box, CircularProgress } from '@mui/material';
import { API_URL } from '../../utils/config';
import SEO from '../SEO/SEO';
import { createMetaDescription } from '../../services/seoService';
import { FONTS } from '../../theme/tokens';
import { removeLeadingDuplicateHeading } from './pageContentUtils';
import { CMS_CONTENT_SX } from './cmsContentStyles';
import { getActiveLanguage } from '../../i18n';

const PageDetail = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLang = getActiveLanguage();

  useEffect(() => {
    fetchPage();
  }, [slug, activeLang, i18n.language]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/pages/${slug}?lang=${encodeURIComponent(activeLang)}`);
      if (!response.ok) {
        if (response.status === 404) {
          navigate('/404');
          return;
        }
        throw new Error(t('pages.loadFailed'));
      }
      const data = await response.json();
      setPage(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        Error: {error}
      </Box>
    );
  }
  if (!page) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        {t('pages.notFound')}
      </Box>
    );
  }
  // Prepare SEO metadata
  const pageTitle = page.page_title;
  const pageDescription = page.meta?.description || createMetaDescription(page.page_text || '', 160);
  const canonicalUrl = `${window.location.origin}/${page.page_slug}`;
  const pageHtml = removeLeadingDuplicateHeading(sanitizeHtml(page.page_text), pageTitle);
  return (
    <>
      {/* Add SEO metadata */}
      <SEO 
        meta={{
          title: `${pageTitle} | EladoreRuffles`,
          description: pageDescription,
          canonical: canonicalUrl
        }}
        structuredData={page.structuredData}
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontFamily: FONTS.heading, fontWeight: 600, mb: { xs: 3, md: 4 } }}>
          {page.page_title}
        </Typography>
        {page.page_text ? (
          <Box
            data-testid="cms-page-content"
            sx={CMS_CONTENT_SX}
            dangerouslySetInnerHTML={{ __html: pageHtml }}
          />
        ) : (
          <Typography>{t('pages.noContent')}</Typography>
        )}
      </Box>
    </Container>
    </>
  );
};
export default PageDetail;
